import React from 'react';
import { Formik, useFormikContext } from 'formik';
import { Accordion, AccordionSummary, AccordionDetails, Typography, Stack } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { sxConfig } from './workspace.style';
import { AutoCompleteSelectUI, FormLabelUI } from '../../Interface';
import JokerTab from '../../Interface/Joker/JokerTab';
import { JokerFieldTypes } from '../../../config/joker';
import { getJokerFieldType } from '../../../utils/helper';
import Yup from '../../../utils/yup';

const ProjectJokerFields = ({ jokerGroups, onJokerFieldSave }) => {
  const { values, setFieldValue, getFieldMeta, validateForm } = useFormikContext();

  const handleFieldSave = async (field, value) => {
    const { initialValue } = getFieldMeta(field);

    if (value === initialValue) return;

    await setFieldValue(field, value, false);

    const errors = await validateForm({
      ...values,
      [field]: value,
    });

    if (errors[field]) return;

    await onJokerFieldSave(field, value);
  };

  return (
    <Stack spacing={1}>
      {jokerGroups.map((group, index) => (
        <Stack key={index} spacing={1}>
          <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
            {group.name}
          </Typography>

          <JokerTab
            key={index}
            fields={group.jokerFields}
            variant="outlined"
            size="small"
            onFieldSave={handleFieldSave}
          />
        </Stack>
      ))}
    </Stack>
  );
};

const ProjectDetailsSidebar = ({
  autoCompleteOptions,
  selectedValues,
  handleResponsibleChange,
  projectJokerGroups,
  onJokerFieldSave,
}) => {
  const jokerGroups = projectJokerGroups.filter((group) => group.jokerFields.length > 0);

  const jokerInitialValues = Object.fromEntries(
    jokerGroups.flatMap((group) =>
      group.jokerFields.map(({ jokerField }) => [
        jokerField.field,
        selectedValues[jokerField.field] ?? (getJokerFieldType(jokerField) === JokerFieldTypes.DATE ? null : ''),
      ])
    )
  );

  const jokerValidationSchema = Yup.object().shape(
    Object.fromEntries(
      jokerGroups.flatMap((group) =>
        group.jokerFields.map(({ jokerField }) => {
          const fieldType = getJokerFieldType(jokerField);

          return [
            jokerField.field,
            fieldType === JokerFieldTypes.NUMBER ? Yup.number().nullable() : Yup.string().nullable(),
          ];
        })
      )
    )
  );

  return (
    <Stack spacing={2}>
      <Accordion
        defaultExpanded
        sx={{
          ...sxConfig.card,
          '&.Mui-expanded': {
            margin: 0,
          },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={700}>Projektadatok</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AutoCompleteSelectUI
            label={<FormLabelUI text="Felelős" />}
            variant="outlined"
            size="small"
            dataset={autoCompleteOptions.user}
            disableClearable
            selectedValue={selectedValues.responsiblePersonId}
            selectedLabelValue={selectedValues?.responsiblePerson?.nickName}
            onChange={handleResponsibleChange}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded sx={sxConfig.card}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={700}>Kiegészítő adatok</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {jokerGroups.length > 0 && (
            <Formik
              initialValues={jokerInitialValues}
              validationSchema={jokerValidationSchema}
              validateOnChange={false}
              enableReinitialize
              onSubmit={() => {}}
            >
              <ProjectJokerFields jokerGroups={jokerGroups} onJokerFieldSave={onJokerFieldSave} />
            </Formik>
          )}
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};

export default ProjectDetailsSidebar;
